import { useCallback, useEffect, useReducer, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import type { SavInspectResponse } from '../types/gotypes'

/**
 * The SPSS import wizard's state machine (constructs + SAV import epic, T12).
 *
 *   idle ──inspect──▶ inspecting ──▶ reviewing ──run──▶ starting ──202──▶ running ──▶ completed
 *     ▲                   │  (upload failed)   ▲            │ (409/4xx)            └──▶ failed
 *     └───────────────────┘                    └────────────┘
 *
 * The file is uploaded once, to `POST /import-sav/inspect`, which stages it
 * and returns Carbon's plan. The job checks (case counts, name conflicts) are
 * made by `POST /:importId/run`; a refusal comes back as a 409 that is kept on
 * the state as `conflict` so the review step can show it and, for existing
 * variable names in add mode, offer "Overwrite existing variables". A queued
 * import runs in the background and its progress is read from the import row
 * every POLL_INTERVAL_MS.
 */

export type SavImportMode = 'new' | 'add' | 'topup'

export type SavImportStage = 'idle' | 'inspecting' | 'reviewing' | 'starting' | 'running' | 'completed' | 'failed'

/** One constructed variable rebuilt after the import (T16). */
export interface SavConstructResult {
  name: string
  kind?: 'user' | 'dataFile' | string
  status: string
  error?: string
}

/** GET /job/:customer/import-sav/:importId/progress. */
export interface SavImportProgress {
  status: 'inspected' | 'queued' | 'running' | 'completed' | 'failed' | string
  phase: string
  phaseLabel: string
  variablesDone: number
  variablesTotal: number
  warnings: string[] | null
  constructResults: SavConstructResult[] | null
  error?: string
}

/** A 409 from the run route that the review step explains. */
export interface SavRunConflict {
  error: 'name_conflict' | 'case_count_mismatch' | 'job_not_empty' | 'job_not_found' | string
  /** The mode the conflict was found for; another mode is checked afresh. */
  mode: SavImportMode
  jobName: string
  jobCases?: number
  fileCases?: number
  constructs?: string[]
  existing?: string[]
}

export interface SavRunParams {
  mode: SavImportMode
  jobName: string
  jobDisplayName?: string
  customerId?: string
  overwriteExisting?: boolean
}

export interface SavImportState {
  stage: SavImportStage
  importId: string | null
  inspect: SavInspectResponse | null
  conflict: SavRunConflict | null
  progress: SavImportProgress | null
  /** The run as it was queued (for the report). */
  run: SavRunParams | null
  error: string | null
}

export const initialSavImportState: SavImportState = {
  stage: 'idle',
  importId: null,
  inspect: null,
  conflict: null,
  progress: null,
  run: null,
  error: null,
}

export type SavImportAction =
  | { type: 'inspectStart' }
  | { type: 'inspectDone'; importId: string; inspect: SavInspectResponse }
  | { type: 'inspectFailed'; error: string }
  | { type: 'runStart' }
  | { type: 'runConflict'; conflict: SavRunConflict }
  | { type: 'runRefused'; error: string }
  | { type: 'runQueued'; run: SavRunParams }
  | { type: 'progress'; progress: SavImportProgress }
  | { type: 'pollFailed'; error: string }
  | { type: 'reset' }

export function savImportReducer(state: SavImportState, action: SavImportAction): SavImportState {
  switch (action.type) {
    case 'inspectStart':
      return { ...initialSavImportState, stage: 'inspecting' }
    case 'inspectDone':
      return { ...initialSavImportState, stage: 'reviewing', importId: action.importId, inspect: action.inspect }
    case 'inspectFailed':
      return { ...initialSavImportState, error: action.error }
    case 'runStart':
      return { ...state, stage: 'starting', conflict: null, error: null }
    case 'runConflict':
      return { ...state, stage: 'reviewing', conflict: action.conflict }
    case 'runRefused':
      return { ...state, stage: 'reviewing', error: action.error }
    case 'runQueued':
      return { ...state, stage: 'running', run: action.run, progress: null }
    case 'progress': {
      const { progress } = action
      if (progress.status === 'completed') return { ...state, stage: 'completed', progress }
      if (progress.status === 'failed') {
        return { ...state, stage: 'failed', progress, error: progress.error || 'The import failed' }
      }
      return { ...state, progress }
    }
    case 'pollFailed':
      return { ...state, stage: 'failed', error: action.error }
    case 'reset':
      return initialSavImportState
  }
}

/** Run-route 409 bodies that describe the job rather than a transient state. */
const CONFLICT_CODES = new Set(['name_conflict', 'case_count_mismatch', 'job_not_empty', 'job_not_found'])

/**
 * Whether the review step may start an import in `mode`: the plan has no
 * blocking errors and no conflict found for this mode stands in the way.
 * Registry constructs are never overwritten; existing variables only with
 * `overwriteExisting`; a case-count mismatch or an unusable job blocks.
 */
export function canRunImport(
  state: SavImportState,
  mode: SavImportMode,
  overwriteExisting: boolean,
  jobName?: string,
): boolean {
  if (state.stage !== 'reviewing' || !state.importId || !state.inspect) return false
  if ((state.inspect.errors ?? []).length > 0) return false
  const conflict = activeConflict(state, mode, jobName)
  if (!conflict) return true
  if (conflict.error === 'name_conflict') {
    if ((conflict.constructs ?? []).length > 0) return false
    return overwriteExisting
  }
  return false
}

/** The stored conflict when it was found for this mode (and job, when given). */
export function activeConflict(state: SavImportState, mode: SavImportMode, jobName?: string): SavRunConflict | null {
  const conflict = state.conflict
  if (!conflict || conflict.mode !== mode) return null
  if (jobName !== undefined && conflict.jobName !== jobName) return null
  return conflict
}

export const POLL_INTERVAL_MS = 2000
/** Consecutive progress-read failures before the wizard stops waiting. */
export const MAX_POLL_ERRORS = 5
/**
 * How long the wizard waits for one import. The worker's own timeout is 3 h;
 * a worker that dies leaves the row `running` forever, so the poller gives up
 * a little after that.
 */
export const MAX_POLL_MS = 3 * 60 * 60 * 1000 + 15 * 60 * 1000

export interface UseSavImportOptions {
  maxPollMs?: number
}

export const useSavImport = (customer: string, options: UseSavImportOptions = {}) => {
  const [state, dispatch] = useReducer(savImportReducer, initialSavImportState)
  const maxPollMs = options.maxPollMs ?? MAX_POLL_MS

  const stateRef = useRef(state)
  stateRef.current = state
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Bumped to cancel an in-flight poll loop (reset, unmount, a new run). */
  const generationRef = useRef(0)

  const stopPolling = useCallback(() => {
    generationRef.current += 1
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // Lifecycle cleanup only: stop polling when the page goes away.
  useEffect(() => stopPolling, [stopPolling])

  const base = `${API_BASE_URL}/job/${customer}/import-sav`

  const inspect = useCallback(
    async ({ file, blnFile }: { file: File; blnFile?: File }): Promise<boolean> => {
      stopPolling()
      dispatch({ type: 'inspectStart' })
      const form = new FormData()
      form.append('file', file)
      if (blnFile) form.append('bln_file', blnFile)
      try {
        const res = await axios.post<{ importId: string; inspect: SavInspectResponse }>(`${base}/inspect`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        dispatch({ type: 'inspectDone', importId: res.data.importId, inspect: res.data.inspect })
        return true
      } catch (err) {
        // 422: the file was inspected and refused; the plan carries the errors.
        const response = (err as { response?: { status?: number; data?: { importId?: string; inspect?: SavInspectResponse } } })
          .response
        if (response?.status === 422 && response.data?.importId && response.data.inspect) {
          dispatch({ type: 'inspectDone', importId: response.data.importId, inspect: response.data.inspect })
          return true
        }
        dispatch({ type: 'inspectFailed', error: extractErrorMessage(err) || 'Upload failed' })
        return false
      }
    },
    [base, stopPolling],
  )

  const poll = useCallback(
    (importId: string, generation: number, startedAt: number, errors: number) => {
      timerRef.current = setTimeout(async () => {
        timerRef.current = null
        if (generation !== generationRef.current) return
        let nextErrors = errors
        try {
          const res = await axios.get<SavImportProgress>(`${base}/${importId}/progress`)
          if (generation !== generationRef.current) return
          nextErrors = 0
          dispatch({ type: 'progress', progress: res.data })
          if (res.data.status === 'completed' || res.data.status === 'failed') return
        } catch (err) {
          if (generation !== generationRef.current) return
          nextErrors = errors + 1
          if (nextErrors >= MAX_POLL_ERRORS) {
            dispatch({
              type: 'pollFailed',
              error: `Lost contact with the import (${extractErrorMessage(err) || 'no response'}). It may still be running.`,
            })
            return
          }
        }
        if (Date.now() - startedAt >= maxPollMs) {
          dispatch({
            type: 'pollFailed',
            error: 'Stopped waiting for the import: it has not finished in the expected time. It may have stopped.',
          })
          return
        }
        poll(importId, generation, startedAt, nextErrors)
      }, POLL_INTERVAL_MS)
    },
    [base, maxPollMs],
  )

  const run = useCallback(
    async (params: SavRunParams): Promise<boolean> => {
      const importId = stateRef.current.importId
      if (!importId) return false
      stopPolling()
      dispatch({ type: 'runStart' })
      const body = {
        mode: params.mode,
        jobName: params.jobName,
        ...(params.jobDisplayName ? { jobDisplayName: params.jobDisplayName } : {}),
        ...(params.customerId ? { customerId: params.customerId } : {}),
        ...(params.overwriteExisting !== undefined ? { overwriteExisting: params.overwriteExisting } : {}),
      }
      try {
        await axios.post(`${base}/${importId}/run`, body)
      } catch (err) {
        const response = (err as { response?: { status?: number; data?: Record<string, unknown> } }).response
        const code = typeof response?.data?.error === 'string' ? (response.data.error as string) : ''
        if (response?.status && (response.status === 409 || response.status === 404) && CONFLICT_CODES.has(code)) {
          const data = response.data as Partial<SavRunConflict>
          dispatch({
            type: 'runConflict',
            conflict: { ...data, error: code, mode: params.mode, jobName: params.jobName },
          })
          return false
        }
        const message = code === 'job_busy'
          ? 'job_busy: another import or construct change is writing to this job. Try again when it has finished.'
          : extractErrorMessage(err) || 'The import could not be started'
        dispatch({ type: 'runRefused', error: message })
        return false
      }
      dispatch({ type: 'runQueued', run: params })
      const generation = generationRef.current
      poll(importId, generation, Date.now(), 0)
      return true
    },
    [base, poll, stopPolling],
  )

  const reset = useCallback(() => {
    stopPolling()
    dispatch({ type: 'reset' })
  }, [stopPolling])

  return { state, inspect, run, reset }
}

export default useSavImport
